import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { Skeleton } from '@vaiu/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@vaiu/ui/collapsible';
import {
  Inbox,
  ChevronDown,
  ChevronUp,
  DollarSign,
  FileText,
  MessageCircle,
  Bell,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Package,
  User,
  Store,
  RefreshCw
} from 'lucide-react';
import { platformApi } from '@/services/platformApi';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import TokenRenderer from '@/components/shared/TokenRenderer';
import ReleaseEscrowInboxAction from '@/components/inbox/ReleaseEscrowInboxAction';
import DepositInboxAction from '@/components/inbox/DepositInboxAction';
import { i18n } from '@/utils/i18n';
import { filterRevealedTokens, filterUnpaidInvoices, filterEscrowedTokensAwaitingRelease, InboxTransaction } from '@/utils/inboxFilters';
import { TRANSACTION_FETCH_LIMIT, INBOX_MAX_HEIGHT } from '@/utils/inboxConstants';

interface InboxItem {
  id: string;
  type: 'token' | 'message' | 'announcement' | 'conversation';
  category: 'action_required' | 'message' | 'announcement';
  title: string;
  description: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
  rawData?: any; // Original token/data for rendering
  merchantId?: string;
  merchantName?: string;
  messageType?: 'announcement' | 'conversation';
  conversationId?: string;
  transactionId?: string;
}

interface UnifiedInboxProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export const UnifiedInbox: React.FC<UnifiedInboxProps> = ({ 
  collapsed = false, 
  onToggle 
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [v2ApiError, setV2ApiError] = useState<Error | null>(null);
  const observerRef = useRef<IntersectionObserver>();
  const lastItemRef = useRef<HTMLDivElement>(null);
  const hasAutoExpandedRef = useRef(false); // Track if we've auto-expanded after initial load
  const userManuallyCollapsedRef = useRef(false); // Track if user manually collapsed
  
  // Counters
  const [counts, setCounts] = useState({
    actionRequired: 0,
    notifications: 0,
    unread: 0
  });

  // Development mode check for debug logging
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Load inbox items
  const loadInboxItems = useCallback(async (pageNum: number = 1, refresh: boolean = false) => {
    if (loading || (!hasMore && !refresh)) return;
    
    if (isDevelopment) {
      console.log('UnifiedInbox: Loading items, page:', pageNum, 'refresh:', refresh);
    }
    
    try {
      setLoading(true);
      if (refresh) {
        setRefreshing(true);
        setV2ApiError(null); // Reset error state on refresh
      }
      
      const allItems: InboxItem[] = [];
      let summaryTotalUnreadCount: number | null = null;
      
      // 1. Load contacts summary using v2 API (ONLY endpoint that exists - per API Reference Manual)
      // Reference: API-REFERENCE-MANUAL.md line 3524 - "This is the recommended endpoint for building a messaging inbox view"
      // NOTE: There is NO v1 inbox endpoint - /api/v1/user/messages/inbox does NOT exist
      try {
        // Use v2 contacts-summary endpoint - only show contacts with unread items
        // Note: This endpoint doesn't support pagination, it returns all contacts with unread items
        // sorted by unread activity (most recent first)
        const summaryResponse = await platformApi.getContactsSummary(false); // includeRead = false
        summaryTotalUnreadCount = summaryResponse.totalUnreadCount;
        setV2ApiError(null); // Clear error if successful
        
        // Transform contacts to InboxItem format
        summaryResponse.contacts.forEach((contact) => {
          // Add message item if unread messages exist
          if (contact.unreadMessagesCount > 0) {
            allItems.push({
              id: `message_${contact.contactId}`,
              type: 'conversation',
              category: 'message',
              title: contact.contactProfile.name,
              description: `${contact.unreadMessagesCount} unread message${contact.unreadMessagesCount !== 1 ? 's' : ''}`,
              timestamp: contact.latestMessageDate || new Date().toISOString(),
              read: false,
              actionUrl: `/messages?contactId=${contact.contactId}`,
              rawData: contact,
              merchantId: contact.contactId,
              merchantName: contact.contactProfile.name,
              messageType: 'conversation'
            });
          }
          
          // Add post item if unread posts exist
          if (contact.unreadPostsCount > 0) {
            allItems.push({
              id: `post_${contact.contactId}`,
              type: 'announcement',
              category: 'announcement',
              title: contact.contactProfile.name,
              description: `${contact.unreadPostsCount} unread post${contact.unreadPostsCount !== 1 ? 's' : ''}`,
              timestamp: contact.latestPostDate || new Date().toISOString(),
              read: false,
              actionUrl: `/messages?contactId=${contact.contactId}&type=posts`,
              rawData: contact,
              merchantId: contact.contactId,
              merchantName: contact.contactProfile.name,
              messageType: 'announcement'
            });
          }
        });
        
        if (isDevelopment) {
          console.log('UnifiedInbox: Loaded contacts summary:', {
            contactsCount: summaryResponse.contacts.length,
            totalUnreadCount: summaryResponse.totalUnreadCount,
            itemsCreated: allItems.length
          });
        }
      } catch (summaryError: any) {
        // V2 API may not be implemented yet - log but continue with tokens/invoices
        const error = summaryError instanceof Error ? summaryError : new Error(String(summaryError));
        setV2ApiError(error);
        if (isDevelopment) {
          console.warn('⚠️ V2 contacts-summary endpoint not available - continuing with tokens/invoices only:', error.message);
        }
        // Continue with tokens/invoices only if v2 API fails
      }
      
      // 2. Fetch transactions from the API (for action-required items)
      const transactionsResponse = await platformApi.transactions.getTransactions({
        limit: TRANSACTION_FETCH_LIMIT
      }).catch((error) => {
        console.error('Failed to fetch transactions:', error);
        return { transactions: [], pending: [], completed: [] };
      });
      
      // Check all transactions, not just pending (redeemed invoices might be in other arrays)
      // Combine all transaction arrays
      const combinedTransactions: InboxTransaction[] = [
        ...(transactionsResponse.transactions || []),
        ...(transactionsResponse.pending || []),
        ...(transactionsResponse.completed || [])
      ] as InboxTransaction[];
      
      // Deduplicate transactions by key/id to prevent duplicate keys in React
      // Same transaction might appear in multiple arrays (transactions, pending, completed)
      const transactionMap = new Map<string, InboxTransaction>();
      combinedTransactions.forEach((tx: InboxTransaction) => {
        const uniqueKey = tx.key || tx.tokenKey || tx._id || tx.id;
        if (uniqueKey && !transactionMap.has(uniqueKey)) {
          transactionMap.set(uniqueKey, tx);
        }
      });
      const allTransactions: InboxTransaction[] = Array.from(transactionMap.values());
      
      if (isDevelopment) {
        console.log('UnifiedInbox: Transaction summary:', {
          totalTransactions: allTransactions.length,
          pendingCount: transactionsResponse.pending?.length || 0,
          completedCount: transactionsResponse.completed?.length || 0,
          allTransactionsCount: transactionsResponse.transactions?.length || 0
        });
      }
      
      // Filter for different types of actionable items using utility functions
      const revealedTokens = {
        tokens: filterRevealedTokens(allTransactions, user)
      };
      
      const unpaidInvoices = {
        tokens: filterUnpaidInvoices(allTransactions, user)
      };
      
      // Keep the old variable name for compatibility
      const pendingInvoices = unpaidInvoices;
      
      const escrowedTokens = {
        tokens: filterEscrowedTokensAwaitingRelease(allTransactions, user)
      };
      
      if (isDevelopment) {
        console.log('UnifiedInbox: Items needing action:', {
          revealedTokens: revealedTokens?.tokens?.length || 0,
          pendingInvoices: pendingInvoices?.tokens?.length || 0,
          escrowedTokens: escrowedTokens?.tokens?.length || 0,
          totalItemsBeforeSort: allItems.length
        });
      }
      
      // Process revealed tokens awaiting redemption/deposit
      if (revealedTokens.tokens && revealedTokens.tokens.length > 0) {
        revealedTokens.tokens.forEach((token: InboxTransaction) => {
          // Skip if no valid timestamp
          const timestamp = token.createdAt || token.timestamp;
          if (!timestamp) return;
          
          // Get token key for URL
          const tokenKey = token.key || token.tokenKey || token._id || token.id;
          if (!tokenKey) {
            if (isDevelopment) {
              console.warn('Revealed token missing key:', token);
            }
            return;
          }
          
          // Check if token has an order in payload
          const hasOrder = token.payload?.order || token.data?.payload?.order;
          
          // Determine title and description based on token type
          let title: string;
          let description: string;
          
          if (hasOrder) {
            title = 'View Order';
            description = `Order from ${token.senderName || token.fromUser || 'Customer'} - ${token.message || 'Review and process order'}`;
          } else {
            // Cash token - user needs to deposit/redeem it
            title = i18n.__('inbox.deposit.badge');
            const amount = token.amount ? `${token.amount} ${token.currency || 'USD'}` : '';
            description = amount 
              ? `Cash token from ${token.senderName || token.fromUser || 'Someone'} - ${amount} ready to deposit`
              : token.message || `Cash token from ${token.senderName || token.fromUser || 'Someone'} ready to deposit`;
          }
          
          allItems.push({
            id: `revealed_${tokenKey}`,
            type: 'token',
            category: 'action_required',
            title: title,
            description: description,
            timestamp: timestamp,
            read: false, // Always show as unread until redeemed/deposited
            // Prefer redeem flow; deposit UI is inline via DepositInboxAction.
            actionUrl: hasOrder ? `/transactions/${tokenKey}` : `/redeem/${tokenKey}`,
            rawData: { ...token, hasOrder, isDepositToken: !hasOrder }
          });
        });
      }
      
      // Process pending invoices (payment requests)
      if (pendingInvoices.tokens) {
        pendingInvoices.tokens.forEach((invoice: InboxTransaction) => {
          // Skip if no valid timestamp
          const timestamp = invoice.createdAt || invoice.timestamp;
          if (!timestamp) return;
          
          // Use invoice key for the URL (route expects /invoice/:invoiceCode where invoiceCode is the key)
          const invoiceKey = invoice.key || invoice.tokenKey || invoice._id || invoice.id;
          if (!invoiceKey) {
            if (isDevelopment) {
              console.warn('Invoice missing key/tokenKey/_id/id:', invoice);
            }
            return;
          }
          
          allItems.push({
            id: `invoice_${invoiceKey}`,
            type: 'token',
            category: 'action_required',
            title: 'Pay Invoice',
            description: invoice.message || `Invoice from ${invoice.merchantName || invoice.senderName || 'Someone'}`,
            timestamp: timestamp,
            read: false, // Always show as unread until paid
            actionUrl: `/invoice/${invoiceKey}`, // Route is /invoice/:invoiceCode (singular, uses key)
            rawData: invoice
          });
        });
      }
      
      // Process escrowed tokens awaiting release approval
      if (escrowedTokens.tokens) {
        escrowedTokens.tokens.forEach((token: InboxTransaction) => {
          // Skip if no valid timestamp
          const timestamp = token.redeemedAt || token.createdAt || token.timestamp;
          if (!timestamp) return;
          
          allItems.push({
            id: `escrow_${token._id || token.id || token.key}`,
            type: 'token',
            category: 'action_required',
            title: i18n.__('inbox.escrow.releaseButton'),
            description: i18n.__('inbox.escrow.taskDescription', {
              recipient: token.recipientName || token.counterparty?.name || i18n.__('inbox.escrow.recipientFallback'),
            }),
            timestamp: timestamp,
            read: false,
            rawData: { ...token, isEscrowRelease: true }
          });
        });
      }
      
      // Deduplicate items by id to prevent duplicate React keys
      const itemsMap = new Map<string, InboxItem>();
      allItems.forEach((item) => {
        if (!itemsMap.has(item.id)) {
          itemsMap.set(item.id, item);
        }
      });
      const deduplicatedItems = Array.from(itemsMap.values());
      
      // Sort all items by timestamp (newest first)
      deduplicatedItems.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      // Since V2 API returns all contacts and we fetch all transactions upfront,
      // disable pagination (all data is loaded in first request)
      setHasMore(false);
      
      if (refresh) {
        setItems(deduplicatedItems);
        setPage(1);
      } else {
        setItems(prev => pageNum === 1 ? deduplicatedItems : [...prev, ...deduplicatedItems]);
      }
      
      // Show success toast on refresh
      if (refresh) {
        toast({
          title: 'Refreshed',
          description: `Loaded ${deduplicatedItems.length} item${deduplicatedItems.length !== 1 ? 's' : ''}`,
          duration: 2000
        });
      }
      
      // Calculate counts from deduplicated items
      const actionRequiredCount = deduplicatedItems.filter(item => item.category === 'action_required').length;
      const unreadCount = deduplicatedItems.filter(item => !item.read).length;
      const messageCount = deduplicatedItems.filter(item => item.type === 'message' || item.type === 'conversation' || item.type === 'announcement').length;
      
      setCounts({
        actionRequired: actionRequiredCount,
        notifications: messageCount,
        unread: unreadCount
      });
    } catch (error) {
      console.error('Failed to load inbox items:', error);
      if (pageNum === 1) {
        toast({
          title: 'Error',
          description: 'Failed to load inbox items',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loading, hasMore, toast, user, isDevelopment]);

  // Infinite scroll disabled - V2 API returns all contacts and transactions are fetched upfront
  // No pagination needed since all data is loaded in the first request

  // Initial load
  useEffect(() => {
    loadInboxItems(1);
  }, []);

  // Sync collapsed prop with state (only on initial mount or when prop changes externally)
  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  // Auto-expand if there are messages/items that need attention (only once after initial load, unless user manually collapsed)
  useEffect(() => {
    const hasItemsToDealWith = counts.unread > 0 || counts.actionRequired > 0;
    if (hasItemsToDealWith && isCollapsed && !loading && !hasAutoExpandedRef.current && !userManuallyCollapsedRef.current) {
      setIsCollapsed(false);
      hasAutoExpandedRef.current = true; // Mark as auto-expanded so we don't do it again
    }
  }, [counts.unread, counts.actionRequired, loading, isCollapsed]);

  const handleToggle = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    // Track if user manually collapsed (if collapsing from expanded state)
    if (newCollapsed && !isCollapsed) {
      userManuallyCollapsedRef.current = true;
    }
    onToggle?.();
  };

  const handleRefresh = () => {
    loadInboxItems(1, true);
  };

  const handleItemClick = (item: InboxItem) => {
    if (item.actionUrl) {
      navigate(item.actionUrl);
    }
  };

  const getItemIcon = (type: InboxItem['type']) => {
    switch (type) {
      case 'token':
        return <DollarSign className="w-4 h-4 text-blue-600" />;
      case 'message':
      case 'conversation':
        return <MessageCircle className="w-4 h-4 text-purple-600" />;
      case 'announcement':
        return <Bell className="w-4 h-4 text-orange-600" />;
      default:
        return <Inbox className="w-4 h-4" />;
    }
  };

  const renderInboxItem = (item: InboxItem) => {
    // Handle messages and announcements
    if (item.type === 'announcement' || item.type === 'conversation' || item.type === 'message') {
      const isUnread = !item.read;
      return (
        <div 
          key={item.id} 
          className={`border-b last:border-b-0 p-4 transition-colors cursor-pointer ${
            isUnread ? 'bg-blue-50/50 hover:bg-blue-50' : 'hover:bg-gray-50'
          }`}
          onClick={() => handleItemClick(item)}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              {getItemIcon(item.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm truncate">
                    {item.merchantName || 'Merchant'}
                  </h4>
                  {item.type === 'announcement' && (
                    <Badge variant="secondary" className="text-xs">Announcement</Badge>
                  )}
                  {item.type === 'conversation' && (
                    <Badge variant="outline" className="text-xs">Conversation</Badge>
                  )}
                  {isUnread && (
                    <Badge variant="default" className="text-xs">New</Badge>
                  )}
                </div>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {item.timestamp && !isNaN(new Date(item.timestamp).getTime()) 
                    ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })
                    : 'Recently'}
                </span>
              </div>
              {item.title && item.type === 'announcement' && (
                <p className="text-sm font-medium text-gray-900 mb-1">{item.title}</p>
              )}
              <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Handle tokens requiring action - use TokenRenderer
    if (item.type === 'token' && item.rawData) {
      const isEscrowRelease = Boolean(item.rawData.isEscrowRelease);
      const isDepositToken = Boolean(item.rawData.isDepositToken);
      return (
        <div key={item.id} className="border-b last:border-b-0 bg-amber-50/30 hover:bg-amber-50/50">
          <TokenRenderer 
            transaction={item.rawData}
            showTimestamp={true}
            className="p-4 transition-colors"
            onClick={
              isEscrowRelease || isDepositToken
                ? undefined
                : () => handleItemClick(item)
            }
          />
          <div className="px-4 pb-2 flex items-center gap-2">
            <Badge variant="destructive" className="text-xs">
              {item.title}
            </Badge>
            <span className="text-xs text-gray-500">
              {item.timestamp && !isNaN(new Date(item.timestamp).getTime()) 
                ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })
                : 'Recently'}
            </span>
          </div>
          {isEscrowRelease ? (
            <ReleaseEscrowInboxAction
              token={item.rawData}
              onReleased={() => loadInboxItems(1, true)}
            />
          ) : isDepositToken ? (
            <DepositInboxAction
              token={item.rawData}
              onDeposited={() => loadInboxItems(1, true)}
            />
          ) : null}
        </div>
      );
    }
    
    // Fallback (shouldn't happen)
    return null;
  };

  const renderEmptyState = () => (
    <div className="py-3 px-4 text-center">
      <CheckCircle className="w-6 h-6 mx-auto mb-1.5 text-green-500" />
      <p className="text-sm text-gray-600 font-medium">
        All caught up! No items require your attention right now
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Messages from merchants and tokens requiring action will appear here
      </p>
      {v2ApiError && (
        <p className="text-xs text-amber-600 mt-2">
          Messages unavailable - showing tokens only
        </p>
      )}
    </div>
  );

  const renderLoadingState = () => (
    <div className="space-y-2 py-3 px-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex items-start gap-2.5">
          <Skeleton className="w-4 h-4 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="h-2.5 w-full" />
            <Skeleton className="h-2.5 w-16" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Card className="w-full">
      <Collapsible open={!isCollapsed} onOpenChange={handleToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Inbox className="w-4 h-4" />
                <CardTitle className="text-base">Messages & Inbox</CardTitle>
                {counts.unread > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {counts.unread} {counts.unread === 1 ? 'Unread' : 'Unread'}
                  </Badge>
                )}
                {counts.unread === 0 && counts.actionRequired > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {counts.actionRequired} {counts.actionRequired === 1 ? 'Action' : 'Actions'}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!isCollapsed && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefresh();
                    }}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                  </Button>
                )}
                {isCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </div>
            </div>
            {isCollapsed && (
              <CardDescription className="text-xs mt-1">
                {counts.unread > 0 
                  ? `${counts.unread} ${counts.unread === 1 ? 'unread item' : 'unread items'}`
                  : counts.actionRequired > 0
                  ? `${counts.actionRequired} ${counts.actionRequired === 1 ? 'item needs' : 'items need'} your action`
                  : 'No new items'}
              </CardDescription>
            )}
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <Separator />
          <div className={items.length === 0 ? "" : "max-h-[600px] overflow-y-auto"}>
            {loading && items.length === 0 ? (
              renderLoadingState()
            ) : items.length === 0 ? (
              renderEmptyState()
            ) : (
              <>
                {items.map(renderInboxItem)}
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default UnifiedInbox;
